// The four buddies. Transcribed from SPEC.md §3, §7 (O7, M2), §8. Plain English, no city names.
import type { CharId } from '../types';

export interface CharDef {
  id: CharId;
  name: string;
  role: string;            // 'Stage manager'
  place: string;           // 'the theater'
  placeName: string;       // "Margo's theater"
  placeAt: string;         // 'at the theater' (desktop header)
  acc: string;             // accent
  on: string;              // text on accent
  bg: string;              // page background
  pron: 'she' | 'he'; obj: 'her' | 'him'; poss: 'her' | 'his';
  pickLine: string;        // O7 card line
  bio: string;             // M2 "Who she is"
  assets: { key: string; faces: string; keeps: string; stage: (n: number) => string; chap: (n: number) => string; loop: string | null; poster: string };
}

const A = (ch: CharId, loop: boolean) => ({
  key: `/assets/${ch}/key.jpg`, faces: `/assets/${ch}/faces.jpg`, keeps: `/assets/${ch}/keeps.jpg`,
  stage: (n: number) => `/assets/${ch}/stage${Math.min(7, Math.max(1, n))}.jpg`,
  chap: (n: number) => (n <= 1 ? `/assets/${ch}/key.jpg` : `/assets/${ch}/chap${Math.min(7, n)}.jpg`),
  loop: loop ? `/assets/${ch}/loop1.mp4` : null, poster: `/assets/${ch}/loop1-poster.jpg`,
});

export const C: Record<CharId, CharDef> = {
  sunny: { id: 'sunny', name: 'Sunny', role: 'Stand-up comic', place: 'the comedy club', placeName: "Sunny's club", placeAt: 'at the comedy club',
    acc: '#E3B23C', on: '#1E1604', bg: '#120F0C', pron: 'she', obj: 'her', poss: 'her',
    pickLine: 'Stand-up comic. Roasts your to-do list, always on your side.',
    bio: "Sunny does stand-up comedy. On stage she's sharp. Off stage she loses her keys a lot. She gets you because she's been there.",
    assets: A('sunny', true) },
  margo: { id: 'margo', name: 'Margo', role: 'Stage manager', place: 'the theater', placeName: "Margo's theater", placeAt: 'at the theater',
    acc: '#E0A43A', on: '#1A1206', bg: '#0F0E0C', pron: 'she', obj: 'her', poss: 'her',
    pickLine: 'Stage manager. Calm, direct, keeps the list for you.',
    bio: 'Margo runs the backstage of a small theater. She keeps the whole list so you only have to see the next thing.',
    assets: A('margo', true) },
  arjun: { id: 'arjun', name: 'Arjun', role: 'Art restorer', place: 'the studio', placeName: "Arjun's studio", placeAt: 'at the studio',
    acc: '#A9B8F0', on: '#0E1530', bg: '#0E1018', pron: 'he', obj: 'him', poss: 'his',
    pickLine: 'Art restorer. Patient. One small piece at a time.',
    bio: "Arjun cleans and repairs old wall paintings. He's slow on purpose. Small pieces, one at a time.",
    assets: A('arjun', true) },
  tavo: { id: 'tavo', name: 'Tavo', role: 'Baseball radio announcer', place: 'the radio booth', placeName: "Tavo's booth", placeAt: 'in the radio booth',
    acc: '#F28A44', on: '#1F0E02', bg: '#120D09', pron: 'he', obj: 'him', poss: 'his',
    pickLine: 'Baseball radio voice. Loud, upbeat, cheers every win.',
    bio: 'Tavo calls baseball games on the radio. Loud, funny, and he cheers for every small win.',
    assets: A('tavo', false) },
};

/**
 * Place stages that have a working-together loop video (`/assets/<ch>/loop<stage>.mp4` + `loop<stage>-poster.jpg`).
 * XScene plays the loop for these stages and falls back to the still with a slow drift otherwise.
 * Updated by the media pipeline as new loops are finished.
 */
export const LOOPS: Record<CharId, number[]> = { sunny: [1, 2, 3, 4, 5], margo: [1, 2, 3, 4, 5], arjun: [1, 2, 3, 4, 5], tavo: [1, 2, 3, 4, 5] };
export const loopFor = (ch: CharId, stage: number): { src: string; poster: string } | null =>
  LOOPS[ch].includes(stage) ? { src: `/assets/${ch}/loop${stage}.mp4`, poster: `/assets/${ch}/loop${stage}-poster.jpg` } : null;

/** Before a buddy is picked (O1-O7). */
export const PRE = { acc: '#F2C14E', on: '#1E1604', bg: '#100E0C' };

/** O7 best match from the first tone pick; "also good" = next in this order. */
export const BEST_BY_TONE: Record<string, CharId> = {
  'Warm and kind': 'arjun', 'Straight to the point': 'margo', 'Make me laugh': 'sunny', 'Push me a little': 'tavo',
};
export const ALSO_ORDER: CharId[] = ['margo', 'arjun', 'tavo', 'sunny'];
export function bestMatch(tone: string[] | undefined): { best: CharId; also: CharId } {
  const best = (tone && tone[0] && BEST_BY_TONE[tone[0]]) || 'margo';
  const i = ALSO_ORDER.indexOf(best);
  return { best, also: ALSO_ORDER[(i + 1) % ALSO_ORDER.length] };
}

/** Expression indices on the 3x2 sheet. */
export const FACE = { calm: 0, smile: 1, laugh: 2, work: 3, warm: 4, flustered: 5 } as const;
